import { z } from 'zod';
import { siteDocumentSchema } from '../site/schema';
import {
  siteWorkspaceFileLanguageSchema,
  siteWorkspaceFrameworkSchema,
  siteWorkspaceModeSchema,
  siteWorkspaceTargetKindSchema,
} from '../site-workspace/schema';

export const siteChangeScopeSchema = z.enum(['site', 'page', 'section']);
export const siteChangeActionSchema = z.enum(['added', 'updated', 'removed']);

export type SiteChangeScope = z.infer<typeof siteChangeScopeSchema>;
export type SiteChangeAction = z.infer<typeof siteChangeActionSchema>;

export const siteDocumentDiffEntrySchema = z.object({
  label: z.string().min(1),
  action: siteChangeActionSchema,
  scope: siteChangeScopeSchema,
  pageId: z.string(),
  sectionId: z.string(),
  before: z.string(),
  after: z.string(),
});

export type SiteDocumentDiffEntry = z.infer<typeof siteDocumentDiffEntrySchema>;

export const siteDocumentDiffSchema = z.object({
  text: z.array(siteDocumentDiffEntrySchema),
  structure: z.array(siteDocumentDiffEntrySchema),
  theme: z.array(siteDocumentDiffEntrySchema),
});

export type SiteDocumentDiff = z.infer<typeof siteDocumentDiffSchema>;

export const assistantReplySchema = z.object({
  lead: z.string().min(1),
  result: z.string().min(1),
  nextSuggestions: z.array(z.string().min(1)).max(3).optional(),
});

export type AssistantReply = z.infer<typeof assistantReplySchema>;

export const siteChangeProposalSchema = z.object({
  nextDocument: siteDocumentSchema,
  summary: z.string().min(1),
  assistantReply: assistantReplySchema.optional(),
  diff: siteDocumentDiffSchema,
});

export type SiteChangeProposal = z.infer<typeof siteChangeProposalSchema>;

export const chatContextMessageSchema = z.object({
  role: z.enum(['user', 'assistant']),
  text: z.string(),
});

export type ChatContextMessage = z.infer<typeof chatContextMessageSchema>;

export const aiEditIntentCategorySchema = z.enum([
  'edit',
  'image-regenerate',
  'undo-request',
  'question',
  'unsupported',
  'too-vague',
]);

export type AiEditIntentCategory = z.infer<typeof aiEditIntentCategorySchema>;

export const aiEditIntentStrategySchema = z.enum([
  'workspace-first',
  'structured-first',
  'none',
]);

export type AiEditIntentStrategy = z.infer<typeof aiEditIntentStrategySchema>;

export const aiEditIntentScopeSchema = z.enum([
  'site',
  'page',
  'section',
  'none',
]);

export type AiEditIntentScope = z.infer<typeof aiEditIntentScopeSchema>;

export const aiEditIntentTargetOptionSchema = z.object({
  path: z.string().min(1),
  kind: siteWorkspaceTargetKindSchema,
  label: z.string().min(1),
  aliases: z.array(z.string().min(1)).max(8),
  pageId: z.string().min(1).optional(),
  sectionId: z.string().min(1).optional(),
});

export type AiEditIntentTargetOption = z.infer<typeof aiEditIntentTargetOptionSchema>;

export const aiEditIntentSectionOptionSchema = z.object({
  id: z.string().min(1),
  type: z.string().min(1),
  label: z.string().min(1),
});

export type AiEditIntentSectionOption = z.infer<typeof aiEditIntentSectionOptionSchema>;

export const aiEditIntentWorkspaceContextSchema = z.object({
  framework: siteWorkspaceFrameworkSchema,
  mode: siteWorkspaceModeSchema,
  entryFilePath: z.string().min(1),
  currentPageRouteFilePath: z.string().min(1).optional(),
  aiEditableFilePaths: z.array(z.string().min(1)).max(20).optional(),
  availableTargets: z.array(aiEditIntentTargetOptionSchema).max(40).optional(),
});

export type AiEditIntentWorkspaceContext = z.infer<typeof aiEditIntentWorkspaceContextSchema>;

export const aiEditIntentPageContextSchema = z.object({
  pageId: z.string().min(1),
  title: z.string(),
  path: z.string(),
  sections: z.array(aiEditIntentSectionOptionSchema).max(20),
});

export type AiEditIntentPageContext = z.infer<typeof aiEditIntentPageContextSchema>;

export const resolveAiEditIntentRequestSchema = z.object({
  document: siteDocumentSchema,
  prompt: z.string().min(1),
  currentPageId: z.string().min(1),
  selectedSectionIdHint: z.string().nullable().optional(),
  messages: z.array(chatContextMessageSchema).optional(),
  pageContext: aiEditIntentPageContextSchema,
  workspaceContext: aiEditIntentWorkspaceContextSchema.optional(),
});

export type ResolveAiEditIntentRequest = z.infer<typeof resolveAiEditIntentRequestSchema>;

export const aiEditIntentResultSchema = z.object({
  category: aiEditIntentCategorySchema,
  editStrategy: aiEditIntentStrategySchema,
  scope: aiEditIntentScopeSchema,
  selectedSectionId: z.string(),
  primaryFilePath: z.string(),
  relatedFilePaths: z.array(z.string().min(1)).max(4),
  userMessage: z.string(),
  reasoning: z.string().min(1),
});

export type AiEditIntentResult = z.infer<typeof aiEditIntentResultSchema>;

export const siteChangeWorkspaceFileContextSchema = z.object({
  path: z.string().min(1),
  language: siteWorkspaceFileLanguageSchema,
  purpose: z.string().optional(),
  content: z.string(),
  isEntryFile: z.boolean().optional(),
  isCurrentPageRoute: z.boolean().optional(),
});

export type SiteChangeWorkspaceFileContext = z.infer<typeof siteChangeWorkspaceFileContextSchema>;

export const siteChangeWorkspaceTargetKindSchema = z.enum([
  'selected-file',
  'prompt-file',
  'section-template',
  'section-instance',
  'page-route',
  'helper-file',
  'entry-file',
]);

export type SiteChangeWorkspaceTargetKind = z.infer<typeof siteChangeWorkspaceTargetKindSchema>;

export const siteChangeWorkspaceTargetSchema = z.object({
  path: z.string().min(1),
  role: z.enum(['primary', 'related']),
  kind: siteChangeWorkspaceTargetKindSchema,
  reason: z.string().min(1),
});

export type SiteChangeWorkspaceTarget = z.infer<typeof siteChangeWorkspaceTargetSchema>;

export const siteChangeWorkspaceContextSchema = z.object({
  framework: siteWorkspaceFrameworkSchema,
  mode: siteWorkspaceModeSchema,
  entryFilePath: z.string().min(1),
  currentPageRouteFilePath: z.string().min(1).optional(),
  editableFilePaths: z.array(z.string().min(1)).max(20).optional(),
  selectedFile: siteChangeWorkspaceFileContextSchema.optional(),
  relatedFiles: z.array(siteChangeWorkspaceFileContextSchema).max(4).optional(),
  inferredTargets: z.array(siteChangeWorkspaceTargetSchema).max(6).optional(),
});

export type SiteChangeWorkspaceContext = z.infer<typeof siteChangeWorkspaceContextSchema>;

export const createSiteChangeProposalRequestSchema = z.object({
  document: siteDocumentSchema,
  prompt: z.string().min(1),
  currentPageId: z.string().min(1),
  scope: siteChangeScopeSchema,
  selectedSectionId: z.string().nullable().optional(),
  messages: z.array(chatContextMessageSchema).optional(),
  workspaceContext: siteChangeWorkspaceContextSchema.optional(),
});

export type CreateSiteChangeProposalRequest = z.infer<typeof createSiteChangeProposalRequestSchema>;

export const siteChangeProposalDiagnosticsStageSchema = z.enum([
  'invoke_error',
  'response_schema_error',
  'response_payload_missing',
  'next_document_invalid',
]);

export type SiteChangeProposalDiagnosticsStage = z.infer<typeof siteChangeProposalDiagnosticsStageSchema>;

export const siteChangeProposalDiagnosticsSchema = z.object({
  stage: siteChangeProposalDiagnosticsStageSchema,
  message: z.string().min(1),
  status: z.number().int().nullable().optional(),
  code: z.string().nullable().optional(),
  issues: z.array(z.string()).optional(),
});

export type SiteChangeProposalDiagnostics = z.infer<typeof siteChangeProposalDiagnosticsSchema>;

export const siteChangeProposalResultSchema = z.object({
  provider: z.enum(['openai', 'fallback']),
  proposal: siteChangeProposalSchema,
  providerMessage: z.string().optional(),
  diagnostics: siteChangeProposalDiagnosticsSchema.optional(),
});

export type SiteChangeProposalResult = z.infer<typeof siteChangeProposalResultSchema>;
